import React, { useEffect, useRef, useState } from 'react';

import { joinClassNames } from '../../../../../services/elementHelperService';
import { WIDGET_ANIMATION_TYPE_HAS_DIRECTION } from '../../../../../constants/scenarioConstant';

import { WidgetProps } from './WidgetProps';
import { getBorderStyle, getDropShadowStyle, getFontFamily, getWidgetOpacity } from './helper';

const generateTextStyle = ({
	isShowWidget,
	isExiting,
	style,
	isHover,
	isClickable
}: {
	isShowWidget?: boolean;
	isExiting?: boolean;
	style: WidgetProps.StyleProps;
	isHover?: boolean;
	isClickable?: boolean;
}) => {
	const {
		textAlignment,
		contentSpacing,
		headerLabel: {
			font: headerFont,
			fontSize: headerFontSize,
			fontWeight: headerFontWeight,
			lineHeight: headerLineHeight,
			color: headerColor,
			shadowColor: headerShadowColor,
		},
		textLabel: {
			font: paragraphFont,
			fontSize: paragraphFontSize,
			fontWeight: paragraphFontWeight,
			lineHeight: paragraphLineHeight,
			color: paragraphColor,
			shadowColor: paragraphShadowColor,
		},
		backgroundSettings: {
			backgroundColor,
			hoverBackgroundColor,
		},
		transformSettings: {
			paddingBottom,
			paddingLeft,
			paddingRight,
			paddingTop,
			radiusBottom,
			radiusLeft,
			radiusRight,
			radiusTop,
			hoverRadiusTop,
			hoverRadiusRight,
			hoverRadiusBottom,
			hoverRadiusLeft,
		},
	} = style;

	const { border, hoverBorder } = getBorderStyle(style.backgroundSettings);
	const { dropShadow, hoverDropShadow } = getDropShadowStyle(style.transformSettings);

	const wrapperStyle = {
		filter: "drop-shadow(" + (isHover ? (hoverDropShadow || dropShadow) : dropShadow) + ")",
	} as React.CSSProperties;

	const backgroundStyle = {
		display: isShowWidget || isExiting ? 'flex' : 'none',
		flexDirection: 'column',
		backgroundColor: isHover ? (hoverBackgroundColor || backgroundColor) : backgroundColor,
		border: isHover ? (hoverBorder || border) : border,
		paddingBottom: `${paddingBottom}px`,
		paddingLeft: `${paddingLeft}px`,
		paddingRight: `${paddingRight}px`,
		paddingTop: `${paddingTop}px`,
		borderTopRightRadius: isHover ? `${hoverRadiusTop || radiusTop}px` : `${radiusTop}px`,
		borderBottomRightRadius: isHover ? `${hoverRadiusRight || radiusRight}px` : `${radiusRight}px`,
		borderBottomLeftRadius: isHover ? `${hoverRadiusBottom || radiusBottom}px` : `${radiusBottom}px`,
		borderTopLeftRadius: isHover ? `${hoverRadiusLeft || radiusLeft}px` : `${radiusLeft}px`,
		width: '100%',
		height: '100%',
	} as React.CSSProperties;

	const headerStyle = {
		fontFamily: getFontFamily(headerFont),
		fontSize: `${headerFontSize}px`,
		fontWeight: headerFontWeight,
		lineHeight: `${headerLineHeight}%`,
		textAlign: textAlignment,
		color: headerColor,
		textShadow: `2px 2px ${headerShadowColor}`,
		marginBottom: `${contentSpacing}px`,
	} as React.CSSProperties;

	const paragraphStyle = {
		fontFamily: getFontFamily(paragraphFont),
		fontSize: `${paragraphFontSize}px`,
		fontWeight: paragraphFontWeight,
		lineHeight: `${paragraphLineHeight}%`,
		textAlign: textAlignment,
		color: paragraphColor,
		textShadow: `2px 2px ${paragraphShadowColor}`,
	} as React.CSSProperties;

	return {
		headerStyle,
		paragraphStyle,
		backgroundStyle,
		wrapperStyle,
	};
};

const TextWidget = ({
	id,
	isShowWidget,
	isExiting,
	onExitDone,
	itemRef,
	onClickItem,
	widgetTemplateId,
	style,
	isSafari,
	onContextMenu,
	isClickable,
}: WidgetProps.BaseProps) => {
	const headerRef = useRef<HTMLDivElement | null>(null);
	const paragraphRef = useRef<HTMLDivElement | null>(null);
	const gElementRef = useRef<SVGGElement | null>();
	const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isMountedRef = useRef(false);

	const [isHover, setIsHover] = useState(false);

	const handleMouseEnter = () => {
		setIsHover(true);
	};

	const handleMouseLeave = () => {
		setIsHover(false);
	};

	const {
		headerLabel: { text: header },
		textLabel: { text: paragraph },
		transformSettings: {
			entryAnimation = '',
			entryAnimationDirection = '',
			entryAnimationDuration = 1000,
			entryAnimationDelay = 0,
			exitAnimation = '',
			exitAnimationDirection = '',
			exitAnimationDuration = 1000,
			exitAnimationDelay = 0,
			loopAnimation = '',
			loopAnimationDuration = 1000,
			loopAnimationDelay = 0,
		} = {},
	} = style;

	const isTypewriterEntry = entryAnimation === 'typewriter';
	const isTypewriterExit = exitAnimation === 'typewriter';

	const { headerStyle, paragraphStyle, backgroundStyle, wrapperStyle } = generateTextStyle({ isShowWidget, isExiting, style, isHover, isClickable });

	const onAnimationEnd = (e: React.AnimationEvent<HTMLSpanElement>) => {
		const target = e.target as HTMLElement;
		if (animationTimeoutRef.current) {
			clearTimeout(animationTimeoutRef.current);
		}
		animationTimeoutRef.current = setTimeout(() => {

			if (target.className.includes('text-widget-word')) {
				target.className = "text-widget-word";
			}
			if (target.className.includes('text-widget-char')) {
				target.className = "text-widget-char";
			}
		}, 3000);
	};

	const renderCharacter = (text: string) => {
		return text && text.split(" ").map((word, index) => (
			<React.Fragment key={index}>
				<span className="text-widget-word" style={{ display: 'inline-block'} as React.CSSProperties}>
					{word && word.split('').map((char, charIndex) => (
						<React.Fragment key={charIndex}>
							<span
								key={charIndex}
								className="text-widget-char"
								style={{ display: 'inline-block'} as React.CSSProperties}
							>
								{char}
							</span>
						</React.Fragment>
					))}
				</span>
				{index < text.split(" ").length - 1 && ' '}
			</React.Fragment>
		))};

	const resetAnimationClasses = () => {
		if (headerRef.current) {
			headerRef.current.className = "";
			const words = headerRef.current.querySelectorAll('.text-widget-word');
			words.forEach((word: any) => word.className = "text-widget-word");
			const chars = headerRef.current.querySelectorAll('.text-widget-char');
			chars.forEach((char: any) => char.className = "text-widget-char");
		}
		if (paragraphRef.current) {
			paragraphRef.current.className = "";
			const words = paragraphRef.current.querySelectorAll('.text-widget-word');
			words.forEach((word: any) => word.className = "text-widget-word");
			const chars = paragraphRef.current.querySelectorAll('.text-widget-char');
			chars.forEach((char: any) => char.className = "text-widget-char");
		}
	};

	// Stagger the per-word / per-char reveal so the WHOLE sequence fits inside the
	// widget's animation duration: each item reveals over `perItem` ms and the start
	// times are spread across (duration - perItem), so the last item finishes exactly
	// at baseDelay + duration. (The old `stepDelay * index` grew unbounded with the
	// word/char count and ran far past the widget's timeline.)
	const applyStaggeredAnimation = (
		nodes: HTMLElement[],
		baseClass: string,
		animatedClasses: string,
		baseDelay: number,
		duration: number,
		perItemCap: number,
	) => {
		const count = nodes.length;
		const perItem = Math.min(duration, perItemCap);
		const staggerWindow = Math.max(0, duration - perItem);
		nodes.forEach((node, index) => {
			node.className = joinClassNames(baseClass, animatedClasses);
			node.style.setProperty('animation-duration', `${perItem}ms`);
			const delay = baseDelay + (count > 1 ? (index / (count - 1)) * staggerWindow : 0);
			node.style.setProperty('animation-delay', `${Math.round(delay)}ms`);
		});
	};

	const applyAnimationForWords = ({ element, animatedClasses, animationDelay, animationDuration }: { element: any, animatedClasses: string, animationDelay?: number, animationDuration?: number }) => {
		if (!element) {	return; };
		element.className = "";
		const words = Array.from(element.querySelectorAll('.text-widget-word')) as HTMLElement[];
		applyStaggeredAnimation(words, "text-widget-word", animatedClasses, animationDelay ?? 0, animationDuration ?? 1000, 400);
	}

	const applyAnimationForChars = ({ element, animatedClasses, animationDelay, animationDuration }: { element: any, animatedClasses: string, animationDelay?: number, animationDuration?: number }) => {
		if (!element) {	return; };
		element.className = "";
		const chars = Array.from(element.querySelectorAll('.text-widget-char')) as HTMLElement[];
		applyStaggeredAnimation(chars, "text-widget-char", animatedClasses, animationDelay ?? 0, animationDuration ?? 1000, 300);
	}

	useEffect(() => {
		return () => {
			if (animationTimeoutRef.current) {
				clearTimeout(animationTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		const loopAnimationClass = loopAnimation ? `animate__${loopAnimation}` : '';
		const removeAnimationClasses = () => {
			const classNames =  loopAnimationClass ? `${loopAnimationClass} animate__animated animate__infinite` : "";
			if (loopAnimationClass && gElementRef.current) {
				gElementRef.current.style.setProperty('animation-duration', `${loopAnimationDuration}ms`);
				gElementRef.current.style.setProperty('animation-delay', `${loopAnimationDelay}ms`);
			}
			gElementRef.current && gElementRef.current.setAttribute('class', classNames);
		};

		const element = gElementRef.current;
		element && element.addEventListener('animationend', removeAnimationClasses);
		return () => {
			element && element.removeEventListener('animationend', removeAnimationClasses);
		};
	}, [loopAnimation, entryAnimation, entryAnimationDirection, loopAnimationDuration, loopAnimationDelay]);

	useEffect(() => {
		if (gElementRef.current) {
			const loopAnimationClass = loopAnimation ? `animate__${loopAnimation}` : '';
			const classNames = loopAnimationClass ? `${loopAnimationClass} animate__animated animate__infinite` : "";
			if (loopAnimationClass && gElementRef.current) {
				gElementRef.current.style.setProperty('animation-duration', `${loopAnimationDuration}ms`);
				gElementRef.current.style.setProperty('animation-delay', `${loopAnimationDelay}ms`);
			}
			gElementRef.current.setAttribute('class', classNames);
		}
	}, [loopAnimation, loopAnimationDuration, loopAnimationDelay]);

	useEffect(() => {
		const isCharacterFadeEntry = entryAnimation === 'charactersFade';
		const isWordsFadeEntry = entryAnimation === 'wordsFade' || entryAnimation === 'wordsSlide';
		const isTypewriterEntry = entryAnimation === 'typewriter';
		const direction = WIDGET_ANIMATION_TYPE_HAS_DIRECTION.includes(entryAnimation) ? entryAnimationDirection :"";
		const entryAnimationClass = entryAnimation ? `${entryAnimation === "slideAndFade" ? "animate__fadeIn" : `animate__${entryAnimation}`}${direction}` : '';

		if (entryAnimationClass && !isCharacterFadeEntry && !isWordsFadeEntry && !isTypewriterEntry) {
			resetAnimationClasses();
			if (gElementRef.current) {
				const classNames = entryAnimationClass ? `${entryAnimationClass} animate__animated` : "";
				if (entryAnimationClass && gElementRef.current) {
					gElementRef.current.style.setProperty('animation-duration', `${entryAnimationDuration}ms`);
					gElementRef.current.style.setProperty('animation-delay', `${entryAnimationDelay}ms`);
				}
				gElementRef.current.setAttribute('class', classNames);
			}
		}

		if (isTypewriterEntry) {
			const cleanups: (() => void)[] = [];
			const applyTypewriter = (element: HTMLDivElement | null, text: string) => {
				if (!element || !text) { return; }
				let index = 0;
				let timeoutId: ReturnType<typeof setTimeout>;
				element.innerHTML = '<span style="border-right:0.08em solid currentColor">​</span>';
				const tick = () => {
					index++;
					const displayed = text.substring(0, index);
					if (index < text.length) {
						element.innerHTML = `<span style="border-right:0.08em solid currentColor">${displayed}</span>`;
						timeoutId = setTimeout(tick, 200 - Math.random() * 100);
					} else {
						element.innerHTML = displayed;
					}
				};
				timeoutId = setTimeout(tick, entryAnimationDelay + 200 - Math.random() * 100);
				cleanups.push(() => clearTimeout(timeoutId));
			};
			applyTypewriter(headerRef.current, header);
			applyTypewriter(paragraphRef.current, paragraph);
			return () => cleanups.forEach(c => c());
		}

		resetAnimationClasses();
		if (isWordsFadeEntry) {
			const isSlide = entryAnimation === 'wordsSlide';
			const animationClass = `animate__animated ${isSlide ? 'animate__textSlideIn' : 'animate__textFadeIn'}`;
			applyAnimationForWords({
				element: headerRef.current,
				animatedClasses: animationClass,
				animationDelay: entryAnimationDelay,
				animationDuration: entryAnimationDuration,
			});
			applyAnimationForWords({
				element: paragraphRef.current,
				animatedClasses: animationClass,
				animationDelay: entryAnimationDelay,
				animationDuration: entryAnimationDuration,
			});
			return;
		}
		if (isCharacterFadeEntry) {
			applyAnimationForChars({
				element: headerRef.current,
				animatedClasses: "animate__animated animate__textFadeIn",
				animationDelay: entryAnimationDelay,
				animationDuration: entryAnimationDuration,
			});
			applyAnimationForChars({
				element: paragraphRef.current,
				animatedClasses: "animate__animated animate__textFadeIn",
				animationDelay: entryAnimationDelay,
				animationDuration: entryAnimationDuration,
			});
			return;
		}
	}, [entryAnimation, entryAnimationDirection, entryAnimationDelay, entryAnimationDuration, header, paragraph]);

	useEffect(() => {
		if (!isMountedRef.current) {
			isMountedRef.current = true;
			return;
		}

		const isCharacterFadeExit = exitAnimation === 'charactersFade';
		const isWordsFadeExit = exitAnimation === 'wordsFade' || exitAnimation === 'wordsSlide';
		const isTypewriterExit = exitAnimation === 'typewriter';
		const direction = WIDGET_ANIMATION_TYPE_HAS_DIRECTION.includes(exitAnimation) ? exitAnimationDirection :"";
		const exitAnimationClass = exitAnimation ? `${exitAnimation === "slideAndFade" ? "animate__fadeOut" : `animate__${exitAnimation}`}${direction}` : '';

		if (exitAnimationClass && !isWordsFadeExit && !isCharacterFadeExit && !isTypewriterExit) {
			resetAnimationClasses();
			if (gElementRef.current) {
				const classNames = exitAnimationClass ? `${exitAnimationClass} animate__animated` : "";
				if (exitAnimationClass && gElementRef.current) {
					gElementRef.current.style.setProperty('animation-duration', `${entryAnimationDuration}ms`);
					gElementRef.current.style.setProperty('animation-delay', `${entryAnimationDelay}ms`);
				}
				gElementRef.current.setAttribute('class', classNames);
			}
		}

		if (isTypewriterExit) {
			const applyTypewriterOut = (element: HTMLDivElement | null, text: string) => {
				if (!element || !text) { return; }
				let current = text;
				const tick = () => {
					current = current.substring(0, current.length - 1);
					if (current.length > 0) {
						element.innerHTML = `<span style="border-right:0.08em solid currentColor">${current}</span>`;
						setTimeout(tick, (200 - Math.random() * 100) / 2);
					} else {
						element.innerHTML = '';
					}
				};
				setTimeout(tick, exitAnimationDelay + (200 - Math.random() * 100) / 2);
			};
			applyTypewriterOut(headerRef.current, header);
			applyTypewriterOut(paragraphRef.current, paragraph);
			return;
		}

		resetAnimationClasses();
		if (isWordsFadeExit) {
			const isSlide = exitAnimation === 'wordsSlide';
			applyAnimationForWords({
				element: headerRef.current,
				animatedClasses: `animate__animated ${isSlide ? 'animate__textSlideOut' : 'animate__textFadeOut'}`,
				animationDelay: exitAnimationDelay,
				animationDuration: exitAnimationDuration,
			});
			applyAnimationForWords({
				element: paragraphRef.current,
				animatedClasses: `animate__animated ${isSlide ? 'animate__textSlideOut' : 'animate__textFadeOut'}`,
				animationDelay: exitAnimationDelay,
				animationDuration: exitAnimationDuration,
			});
			return;
		}
		if (isCharacterFadeExit) {
			applyAnimationForChars({
				element: headerRef.current,
				animatedClasses: "animate__animated animate__textFadeOut",
				animationDelay: exitAnimationDelay,
				animationDuration: exitAnimationDuration,
			});
			applyAnimationForChars({
				element: paragraphRef.current,
				animatedClasses: "animate__animated animate__textFadeOut",
				animationDelay: exitAnimationDelay,
				animationDuration: exitAnimationDuration,
			});
			return;
		}
	}, [exitAnimation, exitAnimationDirection, exitAnimationDelay, exitAnimationDuration, header, paragraph]);

	useEffect(() => {
		if (!isExiting) {
			return;
		}
		const gEl = gElementRef.current;
		if (!gEl) {
			onExitDone?.();
			return;
		}

		const isCharacterFadeExit = exitAnimation === 'charactersFade';
		const isWordsFadeExit = exitAnimation === 'wordsFade' || exitAnimation === 'wordsSlide';
		const isTypewriterExit = exitAnimation === 'typewriter';
		const direction = WIDGET_ANIMATION_TYPE_HAS_DIRECTION.includes(exitAnimation) ? exitAnimationDirection : "";
		const exitAnimClass = exitAnimation
			? `${exitAnimation === "slideAndFade" ? "animate__fadeOut" : `animate__${exitAnimation}`}${direction}`
			: '';

		if (!exitAnimClass) {
			onExitDone?.();
			return;
		}

		if (!isWordsFadeExit && !isCharacterFadeExit && !isTypewriterExit) {
			headerRef.current && (headerRef.current.className = "");
			gEl.setAttribute('class', `${exitAnimClass} animate__animated`);
			let settled = false;
			const handleEnd = () => {
				if (!settled) {
					settled = true;
					onExitDone?.();
				}
			};
			const fallback = setTimeout(handleEnd, 2000);
			gEl.addEventListener('animationend', handleEnd, { once: true });
			return () => {
				clearTimeout(fallback);
				gEl.removeEventListener('animationend', handleEnd);
			};
		}

		if (isTypewriterExit) {
			const timeouts: ReturnType<typeof setTimeout>[] = [];
			let done = 0;
			const onElementDone = () => {
				done++;
				if (done >= 2) { onExitDone?.(); }
			};
			const applyTypewriterOut = (element: HTMLDivElement | null, text: string) => {
				if (!element || !text) { onElementDone(); return; }
				let current = text;
				const tick = () => {
					current = current.substring(0, current.length - 1);
					if (current.length > 0) {
						element.innerHTML = `<span style="border-right:0.08em solid currentColor">${current}</span>`;
						timeouts.push(setTimeout(tick, (200 - Math.random() * 100) / 2));
					} else {
						element.innerHTML = '';
						onElementDone();
					}
				};
				timeouts.push(setTimeout(tick, exitAnimationDelay + (200 - Math.random() * 100) / 2));
			};
			applyTypewriterOut(headerRef.current, header);
			applyTypewriterOut(paragraphRef.current, paragraph);
			return () => timeouts.forEach(t => clearTimeout(t));
		}

		// Clear any lingering ENTRY classes (e.g. an entry wordsSlide leaves textSlideIn
		// on the words) before applying the exit, or the entry state replays / conflicts
		// and the text "lights up" before fading out. (The player already did this.)
		resetAnimationClasses();
		if (isWordsFadeExit) {
			const isSlide = exitAnimation === 'wordsSlide';
			applyAnimationForWords({
				element: headerRef.current,
				animatedClasses: `animate__animated ${isSlide ? 'animate__textSlideOut' : 'animate__textFadeOut'}`,
				animationDelay: exitAnimationDelay,
				animationDuration: exitAnimationDuration,
			});
			applyAnimationForWords({
				element: paragraphRef.current,
				animatedClasses: `animate__animated ${isSlide ? 'animate__textSlideOut' : 'animate__textFadeOut'}`,
				animationDelay: exitAnimationDelay,
				animationDuration: exitAnimationDuration,
			});
		}

		if (isCharacterFadeExit) {
			applyAnimationForChars({
				element: headerRef.current,
				animatedClasses: "animate__animated animate__textFadeOut",
				animationDelay: exitAnimationDelay,
				animationDuration: exitAnimationDuration,
			});
			applyAnimationForChars({
				element: paragraphRef.current,
				animatedClasses: "animate__animated animate__textFadeOut",
				animationDelay: exitAnimationDelay,
				animationDuration: exitAnimationDuration,
			});
		}

		// The staggered reveal is time-synced to the widget's exit duration (see
		// applyStaggeredAnimation), so it finishes at exitAnimationDelay +
		// exitAnimationDuration - fire onExitDone right after, NOT count*step (which
		// grew to tens of seconds for long text and stranded the widget "exiting").
		const totalDuration = (exitAnimationDelay || 0) + (exitAnimationDuration || 1000) + 200;
		const timeoutId = setTimeout(() => onExitDone?.(), totalDuration);
		return () => clearTimeout(timeoutId);
	}, [isExiting, exitAnimation, exitAnimationDirection, exitAnimationDelay, exitAnimationDuration, onExitDone, header, paragraph]);

	return (
		<g id={widgetTemplateId} className={`${!isSafari ? 'widget' : ''} widget_${widgetTemplateId}`} style={{ opacity: getWidgetOpacity(style.backgroundSettings, isHover) }}>
			<g ref={itemRef}>
				<g
					ref={r => gElementRef.current = r}
					data-widget-id={id}
					data-template-id={widgetTemplateId}
					onClick={(e) => {
						e.stopPropagation();
						isShowWidget && onClickItem?.(itemRef);
					}}
					onContextMenu={(e) => {
						e.preventDefault();
						onContextMenu?.(itemRef, !!isShowWidget);
						return false;
					}}
					style={wrapperStyle}
				>
					<foreignObject>
						<div
							className="text-widget"
							style={backgroundStyle}
							onMouseEnter={handleMouseEnter}
							onMouseLeave={handleMouseLeave}
							onAnimationEnd={onAnimationEnd}
						>
							{headerStyle && <div ref={headerRef} style={headerStyle}>
								{header && (isTypewriterEntry || isTypewriterExit ? header : renderCharacter(header))}
							</div>}
							<div ref={paragraphRef} style={paragraphStyle}>
								{paragraph && (isTypewriterEntry || isTypewriterExit ? paragraph : renderCharacter(paragraph))}
							</div>
						</div>
					</foreignObject>
				</g>
			</g>
		</g>
	);
};

export default TextWidget;
